import { useEffect, useRef, useState, useCallback } from 'react';
import { getBackendWsOrigin } from '@/api';

function resolveWsBase() {
  const fromEnv = getBackendWsOrigin();
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') {
    return (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host;
  }
  return '';
}

/**
 * Custom hook for WebSocket connections with automatic reconnection
 * @param {string} channel - Channel to subscribe to (reservations, tables, events, global)
 * @param {function} onMessage - Callback for incoming messages
 * @param {boolean} enabled - Whether the connection should be active
 */
export function useWebSocket(channel = 'global', onMessage = null, enabled = true) {
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const pingInterval = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = useCallback(() => {
    const wsBase = resolveWsBase();
    if (!enabled || !wsBase) return;

    try {
      const wsUrl = `${wsBase}/ws/${channel}`;
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log(`[WS] Connected to ${channel}`);
        setIsConnected(true);
        reconnectAttempts.current = 0;

        // Intervalo ping para mantener la conexión
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping cada 30 segundos
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);

          // Omitir mensajes pong
          if (data.type === 'pong') return;

          // Llamar al callback si existe
          if (onMessage) {
            onMessage(data);
          }
        } catch (e) {
          console.warn('[WS] Failed to parse message:', e);
        }
      };

      ws.current.onclose = (event) => {
        console.log(`[WS] Disconnected from ${channel}`, event.code);
        setIsConnected(false);
        clearInterval(pingInterval.current);

        // Intentar reconexión con backoff exponencial
        if (enabled && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          console.log(`[WS] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeout.current = setTimeout(connect, delay);
        }
      };

      ws.current.onerror = (error) => {
        console.error('[WS] Error:', error);
      };
    } catch (error) {
      console.error('[WS] Failed to connect:', error);
    }
  }, [channel, enabled, onMessage]);

  const disconnect = useCallback(() => {
    clearTimeout(reconnectTimeout.current);
    clearInterval(pingInterval.current);
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    }
  }, []);

  const subscribe = useCallback((newChannel) => {
    sendMessage({ type: 'subscribe', channel: newChannel });
  }, [sendMessage]);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    subscribe,
    reconnect: connect,
  };
}

/**
 * Hook specifically for reservation updates
 */
export function useReservationUpdates(onUpdate) {
  const handleMessage = useCallback((data) => {
    if (['reservation_created', 'reservation_cancelled', 'table_reserved', 'table_released', 'availability_changed'].includes(data.type)) {
      if (onUpdate) {
        onUpdate(data);
      }
    }
  }, [onUpdate]);

  return useWebSocket('reservations', handleMessage);
}

/**
 * Hook specifically for table availability updates
 */
export function useTableUpdates(onUpdate) {
  const handleMessage = useCallback((data) => {
    if (['table_reserved', 'table_released', 'availability_changed'].includes(data.type)) {
      if (onUpdate) {
        onUpdate(data);
      }
    }
  }, [onUpdate]);

  return useWebSocket('tables', handleMessage);
}

/**
 * Hook for event updates (registrations, etc.)
 */
export function useEventUpdates(onUpdate) {
  const handleMessage = useCallback((data) => {
    if (data.type === 'event_registration') {
      if (onUpdate) {
        onUpdate(data);
      }
    }
  }, [onUpdate]);

  return useWebSocket('events', handleMessage);
}

export default useWebSocket;
